// =============================================================================
// GOUVERNANCE - Widget Listes & Statuts configurables
// =============================================================================
// Gestion des listes dynamiques par tenant :
// - Types de RDV (visite_amo, chantier, climat...)
// - Statuts de dossier
// - Statuts de client
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import {
  Plus, Trash2, GripVertical, Save, RefreshCw, Calendar, FolderKanban,
  Users, Palette, Check, X, Edit, List, ChevronDown, ChevronRight
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const LISTE_TYPES = [
  { id: "types_rdv", label: "Types de RDV", icon: Calendar, description: "Types d'événements dans le planning (Visite AMO, Chantier...)" },
  { id: "statuts_dossier", label: "Statuts Dossier", icon: FolderKanban, description: "Statuts possibles pour les dossiers métier" },
  { id: "statuts_client", label: "Statuts Client", icon: Users, description: "Statuts possibles pour les clients" },
  { id: "__metier__", label: "Listes métier", icon: List, description: "Toutes les listes configurables du tenant (produits, catégories, options...)" },
];

const COLOR_OPTIONS = [
  { id: "blue", label: "Bleu", class: "bg-blue-500" },
  { id: "cyan", label: "Cyan", class: "bg-cyan-500" },
  { id: "green", label: "Vert", class: "bg-green-500" },
  { id: "emerald", label: "Émeraude", class: "bg-emerald-500" },
  { id: "yellow", label: "Jaune", class: "bg-yellow-500" },
  { id: "amber", label: "Ambre", class: "bg-amber-500" },
  { id: "orange", label: "Orange", class: "bg-orange-500" },
  { id: "red", label: "Rouge", class: "bg-red-500" },
  { id: "purple", label: "Violet", class: "bg-purple-500" },
  { id: "indigo", label: "Indigo", class: "bg-indigo-500" },
  { id: "slate", label: "Gris", class: "bg-slate-500" },
  { id: "zinc", label: "Zinc", class: "bg-zinc-500" },
];

const COLOR_ID_MAP = { "9": "blue", "10": "emerald", "11": "red", "5": "yellow" };

// =============================================================================
// PANEL — Listes métier (toutes les listes du tenant)
// =============================================================================
function MetierListesPanel({ getHeaders }) {
  const [listes, setListes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dirty, setDirty] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { listeType, idx }
  const [newListeType, setNewListeType] = useState("");
  const [addingListe, setAddingListe] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config-listes`, {
        headers: getHeaders(), credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setListes(data.listes || []);
      }
    } catch (e) {
      console.error("Erreur chargement listes métier:", e);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleExpand = (type) => setExpanded(p => ({ ...p, [type]: !p[type] }));

  const updateItems = (type, items) => {
    setListes(prev => prev.map(l => l.type === type ? { ...l, items } : l));
    setDirty(p => ({ ...p, [type]: true }));
  };

  const saveList = async (type) => {
    const liste = listes.find(l => l.type === type);
    if (!liste) return;
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${type}`, {
        method: "PUT",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({ items: liste.items || [] })
      });
      if (res.ok) {
        toast.success(`Liste "${type}" sauvegardée`);
        setDirty(p => ({ ...p, [type]: false }));
      } else {
        toast.error("Erreur sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const deleteList = async (type) => {
    if (!window.confirm(`Supprimer la liste "${type}" ?`)) return;
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${type}`, {
        method: "DELETE", headers: getHeaders(), credentials: "include"
      });
      if (res.ok) {
        setListes(prev => prev.filter(l => l.type !== type));
        toast.success(`Liste "${type}" supprimée`);
      } else {
        toast.error("Erreur suppression");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const addItemToList = (type) => {
    const newItem = { id: `item_${Date.now()}`, label: "" };
    const liste = listes.find(l => l.type === type);
    const updated = [...(liste?.items || []), newItem];
    updateItems(type, updated);
    setEditingCell({ listeType: type, idx: updated.length - 1 });
  };

  const removeItemFromList = (type, idx) => {
    const liste = listes.find(l => l.type === type);
    const updated = (liste?.items || []).filter((_, i) => i !== idx);
    updateItems(type, updated);
  };

  const updateItemField = (type, idx, field, value) => {
    const liste = listes.find(l => l.type === type);
    const updated = (liste?.items || []).map((it, i) => i === idx ? { ...it, [field]: value } : it);
    updateItems(type, updated);
  };

  const createNewListe = async () => {
    const slug = newListeType.trim().toLowerCase().replace(/\s+/g, "_");
    if (!slug) return toast.error("Nom requis");
    if (listes.find(l => l.type === slug)) return toast.error("Cette liste existe déjà");
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${slug}`, {
        method: "PUT",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({ items: [] })
      });
      if (res.ok) {
        setListes(prev => [...prev, { type: slug, items: [], label: newListeType.trim() }]);
        setExpanded(p => ({ ...p, [slug]: true }));
        setNewListeType("");
        setAddingListe(false);
        toast.success(`Liste "${slug}" créée`);
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
      <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Chargement...
    </div>
  );

  return (
    <div className="space-y-3" data-testid="metier-listes-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">{listes.length} liste{listes.length !== 1 ? "s" : ""} configurée{listes.length !== 1 ? "s" : ""}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Recharger
          </Button>
          <Button size="sm" onClick={() => setAddingListe(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-new-liste">
            <Plus className="w-4 h-4 mr-1.5" />
            Nouvelle liste
          </Button>
        </div>
      </div>

      {/* New liste form */}
      {addingListe && (
        <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
          <Input
            value={newListeType}
            onChange={e => setNewListeType(e.target.value)}
            placeholder="nom_de_la_liste (ex: types_produits)"
            className="h-8 bg-zinc-800 border-zinc-700 text-white text-sm flex-1"
            autoFocus
            onKeyDown={e => { if (e.key === "Enter") createNewListe(); if (e.key === "Escape") setAddingListe(false); }}
            data-testid="new-liste-input"
          />
          <Button size="sm" onClick={createNewListe} className="bg-cyan-600 hover:bg-cyan-700 text-white">Créer</Button>
          <Button variant="ghost" size="sm" onClick={() => setAddingListe(false)}><X className="w-4 h-4" /></Button>
        </div>
      )}

      {listes.length === 0 && (
        <div className="text-center py-10 text-zinc-500 text-sm rounded-xl" style={{ border: "1px solid var(--oh-border)" }}>
          Aucune liste métier configurée
        </div>
      )}

      {/* Listes */}
      {listes.map(liste => {
        const isOpen = expanded[liste.type];
        const isDirty = dirty[liste.type];
        const items = liste.items || [];
        return (
          <div key={liste.type} className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--oh-border)" }}>
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
              style={{ background: "var(--oh-bg-elevated)" }}
              onClick={() => toggleExpand(liste.type)}
              data-testid={`liste-header-${liste.type}`}
            >
              {isOpen
                ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />
                : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
              }
              <span className="font-mono text-xs text-cyan-400 w-48 shrink-0">{liste.type}</span>
              <span className="text-sm text-zinc-300 flex-1">{liste.label || ""}</span>
              <Badge className="text-[10px] bg-zinc-700 text-zinc-300 border-0">{items.length} valeur{items.length !== 1 ? "s" : ""}</Badge>
              {isDirty && (
                <Button
                  size="sm"
                  onClick={e => { e.stopPropagation(); saveList(liste.type); }}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white h-7 text-xs"
                  data-testid={`save-liste-${liste.type}`}
                >
                  <Save className="w-3 h-3 mr-1" /> Sauvegarder
                </Button>
              )}
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 text-zinc-500 hover:text-red-400"
                onClick={e => { e.stopPropagation(); deleteList(liste.type); }}
                data-testid={`delete-liste-${liste.type}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Items */}
            {isOpen && (
              <div style={{ borderTop: "1px solid var(--oh-border)" }}>
                <div className="divide-y" style={{ borderColor: "var(--oh-border)" }}>
                  {items.map((item, idx) => {
                    const isEditing = editingCell?.listeType === liste.type && editingCell?.idx === idx;
                    return (
                      <div
                        key={item.id || idx}
                        className="flex items-center gap-3 px-4 py-2.5 group"
                        style={{ background: isEditing ? "var(--oh-bg-surface)" : "var(--oh-bg-elevated)" }}
                        data-testid={`metier-item-${liste.type}-${idx}`}
                      >
                        <GripVertical className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                        {isEditing ? (
                          <div className="flex-1 flex items-center gap-2">
                            <Input
                              value={item.label || ""}
                              onChange={e => updateItemField(liste.type, idx, "label", e.target.value)}
                              className="h-7 bg-zinc-800 border-zinc-700 text-white text-sm"
                              placeholder="Libellé affiché"
                              autoFocus
                              data-testid={`edit-metier-label-${idx}`}
                            />
                            <Input
                              value={item.id || ""}
                              onChange={e => updateItemField(liste.type, idx, "id", e.target.value)}
                              className="h-7 w-44 bg-zinc-800 border-zinc-700 text-zinc-400 text-xs font-mono"
                              placeholder="identifiant"
                              data-testid={`edit-metier-id-${idx}`}
                            />
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-400" onClick={() => setEditingCell(null)} aria-label="Valider la modification">
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex-1 flex items-center gap-3">
                            <span className="text-sm text-white">{item.label || "(sans nom)"}</span>
                            <span className="text-[10px] font-mono text-zinc-600">{item.id}</span>
                            {item.default && <Badge className="text-[10px] bg-cyan-500/20 text-cyan-400 border-0">défaut</Badge>}
                          </div>
                        )}
                        {!isEditing && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-white" onClick={() => setEditingCell({ listeType: liste.type, idx })} aria-label="Modifier l'élément de liste">
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => removeItemFromList(liste.type, idx)} aria-label="Supprimer l'élément de liste">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="text-center py-5 text-zinc-500 text-xs">Aucune valeur</div>
                  )}
                </div>
                <button
                  onClick={() => addItemToList(liste.type)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-zinc-400 hover:text-cyan-400 transition-colors"
                  style={{ background: "var(--oh-bg-surface)", borderTop: "1px solid var(--oh-border)" }}
                  data-testid={`add-metier-item-${liste.type}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter une valeur
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
export default function GouvListesStatuts() {
  const { user: currentUser } = useAuth();
  const [activeList, setActiveList] = useState("types_rdv");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const isMetierTab = activeList === "__metier__";

  const loadList = useCallback(async () => {
    if (isMetierTab) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${activeList}`, {
        headers: getHeaders(), credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setDirty(false);
      }
    } catch (e) {
      console.error("Erreur chargement liste:", e);
    } finally {
      setLoading(false);
    }
  }, [activeList, isMetierTab]);

  useEffect(() => { loadList(); }, [loadList]);

  const saveList = async () => {
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${activeList}`, {
        method: "PUT",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({ items })
      });
      if (res.ok) {
        toast.success("Liste sauvegardée");
        setDirty(false);
      } else {
        toast.error("Erreur de sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const addItem = () => {
    const newItem = { id: `item_${Date.now()}`, label: "", color: "blue" };
    if (activeList === "types_rdv") newItem.color_id = "9";
    setItems([...items, newItem]);
    setEditingIdx(items.length);
    setDirty(true);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
    setDirty(true);
  };

  const activeConfig = LISTE_TYPES.find(l => l.id === activeList);

  return (
    <div className="h-full overflow-auto p-6 space-y-6" data-testid="gouv-listes-statuts">
      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {LISTE_TYPES.map(lt => {
          const Icon = lt.icon;
          const isActive = activeList === lt.id;
          return (
            <button
              key={lt.id}
              onClick={() => { setActiveList(lt.id); setEditingIdx(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800"
              }`}
              data-testid={`tab-${lt.id}`}
            >
              <Icon className="w-4 h-4" />
              {lt.label}
            </button>
          );
        })}
      </div>

      {/* Header + description */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">{activeConfig?.label}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{activeConfig?.description}</p>
        </div>
        {!isMetierTab && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadList} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Recharger
            </Button>
            {dirty && (
              <Button size="sm" onClick={saveList} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-list-btn">
                <Save className="w-4 h-4 mr-1.5" />
                Sauvegarder
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Onglet Listes métier */}
      {isMetierTab ? (
        <MetierListesPanel getHeaders={getHeaders} />
      ) : (
        <>
          {/* Items list */}
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--oh-border)" }}>
            <div className="divide-y" style={{ borderColor: "var(--oh-border)" }}>
              {items.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex items-center gap-3 px-4 py-3 group"
                  style={{ background: editingIdx === idx ? "var(--oh-bg-surface)" : "var(--oh-bg-elevated)" }}
                  data-testid={`list-item-${item.id}`}
                >
                  <GripVertical className="w-4 h-4 text-zinc-600 shrink-0" />

                  {/* Color dot */}
                  <div className="relative">
                    <div
                      className={`w-4 h-4 rounded-full shrink-0 ${COLOR_OPTIONS.find(c => c.id === item.color)?.class || "bg-blue-500"}`}
                    />
                  </div>

                  {/* Label */}
                  {editingIdx === idx ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        value={item.label}
                        onChange={e => updateItem(idx, "label", e.target.value)}
                        className="h-8 bg-zinc-800 border-zinc-700 text-white text-sm"
                        placeholder="Nom de l'élément"
                        autoFocus
                        data-testid={`edit-label-${idx}`}
                      />
                      <Input
                        value={item.id}
                        onChange={e => updateItem(idx, "id", e.target.value)}
                        className="h-8 w-40 bg-zinc-800 border-zinc-700 text-zinc-400 text-xs font-mono"
                        placeholder="identifiant_technique"
                        data-testid={`edit-id-${idx}`}
                      />
                      {/* Color picker */}
                      <div className="flex items-center gap-1">
                        {COLOR_OPTIONS.slice(0, 8).map(c => (
                          <button
                            key={c.id}
                            onClick={() => updateItem(idx, "color", c.id)}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${c.class} ${item.color === c.id ? "border-white scale-110" : "border-transparent opacity-60 hover:opacity-100"}`}
                          />
                        ))}
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-green-400" onClick={() => setEditingIdx(null)}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center gap-3">
                      <span className="text-sm text-white">{item.label || "(sans nom)"}</span>
                      <span className="text-[10px] font-mono text-zinc-600">{item.id}</span>
                      {item.default && <Badge className="text-[10px] bg-cyan-500/20 text-cyan-400 border-0">défaut</Badge>}
                    </div>
                  )}

                  {/* Actions */}
                  {editingIdx !== idx && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-white" onClick={() => setEditingIdx(idx)}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => removeItem(idx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {items.length === 0 && (
                <div className="text-center py-8 text-zinc-500 text-sm">
                  Aucun élément configuré
                </div>
              )}
            </div>

            {/* Add button */}
            <button
              onClick={addItem}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm text-zinc-400 hover:text-cyan-400 transition-colors"
              style={{ background: "var(--oh-bg-surface)", borderTop: "1px solid var(--oh-border)" }}
              data-testid="add-list-item"
            >
              <Plus className="w-4 h-4" />
              Ajouter un élément
            </button>
          </div>

          {dirty && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <Palette className="w-3 h-3" />
              Modifications non sauvegardées
            </p>
          )}
        </>
      )}
    </div>
  );
}
